<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import format from 'date-fns/format';
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { useRoute } from 'vue-router';
import reportsAPI from 'dashboard/api/reports';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import BarChart from 'shared/components/charts/BarChart.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import { DATE_RANGE_OPTIONS } from './constants';

const SUPPORTED_RANGE_IDS = [
  DATE_RANGE_OPTIONS.TODAY.id,
  DATE_RANGE_OPTIONS.YESTERDAY.id,
  DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_30_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_3_MONTHS.id,
  DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id,
];

const DATE_RANGE_BY_ID = Object.values(DATE_RANGE_OPTIONS).reduce(
  (acc, option) => {
    acc[option.id] = option;
    return acc;
  },
  {}
);

const { t } = useI18n();
const route = useRoute();

const selectedDateRangeId = ref(DATE_RANGE_OPTIONS.LAST_7_DAYS.id);
const isLoading = ref(false);
const errorMessage = ref('');
const generatedAt = ref(null);
const totalConversations = ref(0);
const totalQuestions = ref(0);
const clusters = ref([]);

const [showDateRangeMenu, toggleDateRangeMenu] = useToggle(false);

const accountId = computed(() => Number(route.params.accountId || 0));

const selectedDateRange = computed(
  () =>
    DATE_RANGE_BY_ID[selectedDateRangeId.value] ||
    DATE_RANGE_OPTIONS.LAST_7_DAYS
);

const customDateRange = ref([new Date(), new Date()]);

const isCustomDateRange = computed(
  () => selectedDateRangeId.value === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
);

const isValidCustomDateRange = value =>
  Array.isArray(value) &&
  value.length === 2 &&
  value[0] instanceof Date &&
  !Number.isNaN(value[0].getTime()) &&
  value[1] instanceof Date &&
  !Number.isNaN(value[1].getTime());

const isDatePickerPopupClick = target =>
  target instanceof Element && Boolean(target.closest('.mx-datepicker-popup'));

const handleDateMenuClickOutside = event => {
  const target = event?.target;
  if (isDatePickerPopupClick(target)) return;
  toggleDateRangeMenu(false);
};

const selectedDateRangeLabel = computed(() => {
  if (
    isCustomDateRange.value &&
    isValidCustomDateRange(customDateRange.value)
  ) {
    return `${format(customDateRange.value[0], 'dd MMM yy')} - ${format(
      customDateRange.value[1],
      'dd MMM yy'
    )}`;
  }

  return t(selectedDateRange.value.translationKey);
});

const dateRangeMenuItems = computed(() =>
  SUPPORTED_RANGE_IDS.map(id => {
    const option = DATE_RANGE_BY_ID[id];
    return {
      action: 'select-date-range',
      value: id,
      label: t(option.translationKey),
      isSelected: id === selectedDateRangeId.value,
    };
  })
);

const currentPeriod = computed(() => {
  if (
    isCustomDateRange.value &&
    isValidCustomDateRange(customDateRange.value)
  ) {
    return {
      from: getUnixTime(startOfDay(customDateRange.value[0])),
      to: getUnixTime(endOfDay(customDateRange.value[1])),
    };
  }

  const option = selectedDateRange.value;
  const startOffset = option.startOffset ?? option.offset ?? 0;
  const endOffset = option.endOffset ?? option.offset ?? 0;
  const toDate = endOfDay(subDays(new Date(), endOffset));
  const fromDate = startOfDay(subDays(new Date(), startOffset));

  return {
    from: getUnixTime(fromDate),
    to: getUnixTime(toDate),
  };
});

const totalClusteredQuestions = computed(() =>
  clusters.value.reduce((sum, row) => sum + Number(row.count || 0), 0)
);

const displayedTotalQuestions = computed(
  () => totalQuestions.value || totalClusteredQuestions.value
);

const uniqueTopicsCount = computed(() => clusters.value.length);

const breakdownRows = computed(() => {
  const total = totalClusteredQuestions.value || 0;
  return clusters.value.map(row => ({
    ...row,
    count: Number(row.count || 0),
    share: total > 0 ? (Number(row.count || 0) / total) * 100 : 0,
  }));
});

const topChartRows = computed(() => breakdownRows.value.slice(0, 10));

const compactLabel = value => {
  const text = String(value || '').trim();
  if (!text) return '-';
  return text.length > 36 ? `${text.slice(0, 33)}...` : text;
};

const chartCollection = computed(() => ({
  labels: topChartRows.value.map(row =>
    compactLabel(row.example || row.normalized)
  ),
  datasets: [
    {
      label: t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_COUNT'),
      data: topChartRows.value.map(row => row.count),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    },
  ],
}));

const chartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
  },
};

const generatedAtLabel = computed(() => {
  if (!generatedAt.value) {
    return t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET');
  }
  return generatedAt.value.toLocaleString();
});

const conversationLink = conversationId =>
  frontendURL(
    conversationUrl({
      accountId: accountId.value,
      id: conversationId,
    })
  );

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const downloadBreakdownCsv = () => {
  const headers = [
    t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_QUESTION'),
    t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_NORMALIZED'),
    t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_COUNT'),
    t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_SHARE'),
    t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_SAMPLES'),
  ];

  const rows = breakdownRows.value.map(row => [
    row.example || row.normalized,
    row.normalized,
    row.count,
    `${row.share.toFixed(1)}%`,
    (row.sample_conversation_ids || []).join(' | '),
  ]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'top-customer-questions-breakdown',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const fetchData = async () => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await reportsAPI.getTopCustomerQuestions({
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
      limit: 50,
      samplePerCluster: 3,
    });
    totalConversations.value = Number(response.data?.total_conversations || 0);
    totalQuestions.value = Number(response.data?.total_questions || 0);
    clusters.value = Array.isArray(response.data?.clusters)
      ? response.data.clusters
      : [];
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('TOP_CUSTOMER_QUESTIONS_REPORT.FETCH_ERROR');
    totalConversations.value = 0;
    totalQuestions.value = 0;
    clusters.value = [];
  } finally {
    isLoading.value = false;
  }
};

const onSelectDateRange = value => {
  selectedDateRangeId.value = value;
  if (value === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id) return;
  toggleDateRangeMenu(false);
};

const onCustomDateChange = value => {
  if (!isValidCustomDateRange(value)) return;
  customDateRange.value = value;
  toggleDateRangeMenu(false);
};

watch(selectedDateRangeId, async () => {
  await fetchData();
});

watch(customDateRange, async () => {
  if (
    isCustomDateRange.value &&
    isValidCustomDateRange(customDateRange.value)
  ) {
    await fetchData();
  }
});

onMounted(async () => {
  await fetchData();
});
</script>

<template>
  <div class="tq-page">
    <div class="tq-shell">
      <div class="tq-header">
        <div class="tq-header-left">
          <h1 class="tq-title">
            {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.TITLE') }}
          </h1>
        </div>
      </div>

      <div class="tq-toolbar">
        <div
          v-on-click-outside="handleDateMenuClickOutside"
          class="tq-toolbar-left"
        >
          <button
            class="tq-secondary-btn"
            type="button"
            @click="toggleDateRangeMenu()"
          >
            <span>{{ selectedDateRangeLabel }}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m6 9 6 6 6-6"
              />
            </svg>
          </button>

          <div v-if="showDateRangeMenu" class="tq-menu">
            <button
              v-for="item in dateRangeMenuItems"
              :key="item.value"
              class="tq-menu-item"
              :class="{ 'is-selected': item.isSelected }"
              type="button"
              @click="onSelectDateRange(item.value)"
            >
              {{ item.label }}
            </button>
            <div v-if="isCustomDateRange" class="tq-menu-custom">
              <WootDateRangePicker
                show-range
                class="tq-date-picker"
                :value="customDateRange"
                :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
                :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
                @change="onCustomDateChange"
              />
            </div>
          </div>
        </div>

        <button
          class="tq-export-btn"
          type="button"
          @click="downloadBreakdownCsv"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
            />
          </svg>
          <span>{{ t('TOP_CUSTOMER_QUESTIONS_REPORT.EXPORT_CSV') }}</span>
        </button>
      </div>

      <div class="tq-content">
        <p class="tq-desc">
          {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.DESCRIPTION') }}
        </p>

        <div v-if="errorMessage" class="tq-error">
          {{ errorMessage }}
        </div>

        <section class="tq-card">
          <div class="tq-card-header">
            <div class="tq-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="tq-card-title">
                {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.TITLE') }}
              </h2>
            </div>
          </div>

          <div class="tq-section tq-section--no-bottom">
            <div class="tq-metric-row">
              <div class="tq-metric">
                <div class="tq-metric-label">
                  {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.TOTAL_QUESTIONS') }}
                </div>
                <div class="tq-metric-value">
                  {{ displayedTotalQuestions.toLocaleString() }}
                </div>
              </div>
              <div class="tq-metric">
                <div class="tq-metric-label">
                  {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.UNIQUE_TOPICS') }}
                </div>
                <div class="tq-metric-value">
                  {{ uniqueTopicsCount.toLocaleString() }}
                </div>
              </div>
              <div class="tq-metric">
                <div class="tq-metric-label">
                  {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.CONVERSATIONS_SCANNED') }}
                </div>
                <div class="tq-metric-value">
                  {{ totalConversations.toLocaleString() }}
                </div>
              </div>
            </div>

            <div class="tq-chart">
              <div class="tq-chart-title">
                {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.CHART_TITLE') }}
              </div>
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
                class="h-full"
              />
              <div v-else-if="!topChartRows.length" class="tq-empty">
                {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.NO_DATA') }}
              </div>
              <BarChart
                v-else
                :collection="chartCollection"
                :chart-options="chartOptions"
              />
            </div>
          </div>

          <div class="tq-breakdown">
            <div class="tq-breakdown-header">
              <div class="tq-breakdown-title">
                {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_TITLE') }}
              </div>
            </div>

            <div class="tq-table-wrap">
              <table class="tq-table">
                <thead>
                  <tr>
                    <th class="tq-th">
                      {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_QUESTION') }}
                    </th>
                    <th class="tq-th tq-th--num">
                      {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_COUNT') }}
                    </th>
                    <th class="tq-th tq-th--num">
                      {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_SHARE') }}
                    </th>
                    <th class="tq-th">
                      {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_SAMPLES') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in breakdownRows" :key="row.normalized">
                    <td class="tq-td tq-td--question">
                      <div class="tq-q-main">
                        {{ row.example || row.normalized }}
                      </div>
                      <div class="tq-q-sub">
                        {{
                          t('TOP_CUSTOMER_QUESTIONS_REPORT.TABLE_NORMALIZED')
                        }}: {{ row.normalized }}
                      </div>
                    </td>
                    <td class="tq-td tq-td--num">
                      {{ row.count.toLocaleString() }}
                    </td>
                    <td class="tq-td tq-td--num">
                      {{ row.share.toFixed(1) }}%
                    </td>
                    <td class="tq-td">
                      <div class="tq-samples">
                        <a
                          v-for="conversationId in row.sample_conversation_ids ||
                          []"
                          :key="conversationId"
                          class="tq-link"
                          :href="conversationLink(conversationId)"
                          target="_blank"
                          rel="noreferrer"
                        >
                          #{{ conversationId }}
                        </a>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!breakdownRows.length">
                    <td colspan="4" class="tq-td tq-td--empty">
                      {{ t('TOP_CUSTOMER_QUESTIONS_REPORT.NO_DATA') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <p class="tq-footer-note">
          {{
            t('TOP_CUSTOMER_QUESTIONS_REPORT.GENERATED_AT', {
              time: generatedAtLabel,
            })
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tq-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: #ffffff;
  color: #111827;
  overflow: auto;
}
.tq-shell {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
  min-width: 650px;
}

.tq-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}
.tq-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.tq-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.tq-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  position: sticky;
  top: 58px;
  z-index: 49;
}
.tq-toolbar-left {
  position: relative;
}

.tq-secondary-btn {
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  cursor: pointer;
}
.tq-secondary-btn:hover {
  background: #f9fafb;
}
.tq-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.12) 0 14px 38px;
  padding: 6px;
  z-index: 30;
}
.tq-menu-item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.tq-menu-item:hover {
  background: #f3f4f6;
}
.tq-menu-item.is-selected {
  background: #eef2ff;
  color: #1d4ed8;
}

.tq-menu-custom {
  margin-top: 8px;
  border-top: 1px solid #eef0f3;
  padding-top: 8px;
}

.tq-date-picker {
  width: 100%;
}

.tq-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.tq-export-btn:hover {
  background: #f9fafb;
}

.tq-content {
  padding: 16px 32px;
  overflow-x: hidden;
}
.tq-desc {
  margin: 0 0 12px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}
.tq-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.tq-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}
.tq-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.tq-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}
.tq-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.tq-section {
  padding: 16px 20px 20px;
}
.tq-section--no-bottom {
  padding-bottom: 0;
}

.tq-metric-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 1100px) {
  .tq-metric-row {
    grid-template-columns: 1fr;
  }
}
.tq-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.tq-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.tq-metric-value {
  margin-top: 10px;
  font-size: 28px;
  line-height: 32px;
  font-weight: 800;
  color: #111827;
}

.tq-chart {
  height: 384px;
  margin-top: 16px;
}
.tq-chart-title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.tq-empty {
  height: calc(100% - 28px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.tq-breakdown {
  border-top: 1px solid #dfe3e8;
}
.tq-breakdown-header {
  padding: 15px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.tq-breakdown-title {
  font-size: 14px;
  font-weight: 800;
  color: #111827;
}

.tq-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}
.tq-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.tq-th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 800;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
  background: #f8fafc;
  white-space: nowrap;
}
.tq-th--num {
  text-align: right;
}
.tq-td {
  padding: 12px;
  border-bottom: 1px solid #edf1f5;
  color: #111827;
  vertical-align: top;
}
.tq-td--num {
  text-align: right;
  white-space: nowrap;
  vertical-align: middle;
}
.tq-td--question {
  max-width: 520px;
}
.tq-td--empty {
  padding: 24px;
  text-align: center;
  color: #6b7280;
}
.tq-q-main {
  font-weight: 700;
  color: #111827;
}
.tq-q-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}
.tq-samples {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tq-link {
  font-size: 12px;
  font-weight: 700;
  color: #1d4ed8;
  text-decoration: underline;
  text-underline-position: from-font;
}

.tq-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

::global(body.dark) .tq-page,
::global(body.dark) .tq-shell,
::global(body.dark) .tq-card,
::global(body.dark) .tq-header,
::global(body.dark) .tq-toolbar,
::global(body.theme-mix) .tq-page,
::global(body.theme-mix) .tq-shell,
::global(body.theme-mix) .tq-card,
::global(body.theme-mix) .tq-header,
::global(body.theme-mix) .tq-toolbar {
  background: #ffffff !important;
  color: #111827 !important;
}
</style>
